

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace } from "vuex-class"

import vSelect from "vue-select"

import DataModal from "@/components/Config/DataModal.vue"

import { Field, CaseDetails, DataDefinitionWithFields, FieldInstance, DataSourceItem, CaseField } from "@/types"

import api from "@/services/api"
import { createPopper } from "@popperjs/core"

const caseStore = namespace("cases")

const MAX_WIDTH: string = "20vw"

@Component({
	components: {
		"v-select": vSelect,
		"data-modal": () => import("@/components/Config/DataModal.vue")
	}
})
export default class DataSourceSelect extends Vue {
	$refs!: {
		textarea: any;
		dataModal: DataModal;
	}

	@Prop()
	public currentCase!: CaseDetails;

	@Prop({ default: "" })
	public value!: any;

	@Prop({ type: String, default: "" })
	public placeholder!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean;

	@Prop({ type: String, default: "" })
	public dataSourceId!: string;

	@Prop({ type: String, default: "" })
	public superCategoryValue!: string;

	@Prop({ type: String, default: "" })
	public dataSourceTitle!: string;

	@Prop({ type: Boolean, default: false })
	public multiple!: boolean;

	// Load all datasource items regardless of view permissions.
	@Prop({ type: Boolean, default: false })
	public ignorePermissions!: boolean;

	@Prop()
	public field!: Field;

	public dataSource: any = null;
	public dataSourceOptions: any[] = [];
	public selectedItem: any = null;

	public definition: DataDefinitionWithFields | null = null;

	public loading: boolean = true;

	/**
	 * Field that filters the values in this data source
	 */
	private get filterField (): CaseField | null {
		if (this.field && this.field.extensions && this.field.extensions.listFilter && this.field.extensions.listFilterValueField && this.currentCase) {
			return this.currentCase.fields[this.field!.extensions!.listFilterValueField]
		} else {
			return null
		}
	}

	@Watch("filterField.value")
	public async filterFieldValueChanged (selectedValue: string | null) {
		if (this.dataSource.itemCount > 250 && this.field!.extensions!.listFilter && this.field!.extensions!.listFilterValueField) {
			this.dataSourceOptions = selectedValue
				? (await api.dataSourceSearch(this.dataSourceId, this.field!.extensions!.listFilter, selectedValue)).data
				: []
		}
	}

	public get filteredOptions () {
		if (!this.filterField) {
			return this.dataSourceOptions
		} else {
			if (!this.filterField.value || (Array.isArray(this.filterField.value) && this.filterField.value.length === 0)) {
				return this.dataSourceOptions
			}

			const value = this.filterField.value

			const matchId = Array.isArray(value)
				? (id: string) => value.some(v => v.id === id)
				: (id: string) => value === id

			// Yes, double filter. This shouldn't be combined, as it will still include those without fields...
			return this.dataSourceOptions
				.filter(item => !!item.fields)
				.filter(item => Array.isArray(item.fields[this.field.extensions!.listFilter])
					? item.fields[this.field.extensions!.listFilter].some((item: any) => matchId(item.id))
					: matchId(item.fields[this.field.extensions!.listFilter]))
		}
	}

	public get filterFieldTitle (): string {
		if (!this.field || !this.field.extensions ||
			!this.field.extensions.listFilter ||
			!this.field.extensions.listFilterValueField ||
			!this.currentCase) {
			return ""
		}

		const filterField = this.currentCase.fields[this.field.extensions.listFilterValueField]
		if (!filterField) return ""

		return filterField.field.title
	}

	public get isValid (): boolean {
		if (!this.field || !this.field.extensions ||
			!this.field.extensions.listFilter ||
			!this.field.extensions.listFilterValueField ||
			!this.currentCase) {
			return true
		}

		if (!this.selectedItem) {
			return true
		}

		const filterField = this.currentCase.fields[this.field.extensions.listFilterValueField]
		if (!filterField || !filterField.value || (Array.isArray(filterField.value) && filterField.value.length === 0)) {
			return true
		}

		const value = filterField.value
		// Check that a field's value matches the value of the filter field.
		const fieldIsValid = Array.isArray(value)
			? (id: string) => value.some(v => v.id === id)
			: (id: string) => value === id

		// Check that the selected item's value matches the filter value
		const itemIsValid = (item: DataSourceItem) => item.fields
			? Array.isArray(item.fields[this.field.extensions!.listFilter])
				? item.fields[this.field.extensions!.listFilter].some((item: any) => fieldIsValid(item.id))
				: fieldIsValid(item.fields[this.field.extensions!.listFilter])
			: true

		if (this.multiple) {
			return this.selectedItem.every(itemIsValid)
		} else {
			return itemIsValid(this.selectedItem)
		}
	}

	public get isAllowAddingToDataSource (): boolean {
		if (this.field && this.field.extensions && this.field.extensions.allowAddingToDataSource) {
			return true
		}

		return false
	}

	public get dataModalColumns () {
		if (!this.field) return []

		if (!this.definition || !this.definition.fields) {
			return [{
				key: "label",
				label: "Text",
				dataType: "text",
				dataSourceId: null,
				errors: (item: any) => item.label ? null : "Please enter text"
			}]
		}

		const specialFields = this.dataSource.extraFields ? this.dataSource.extraFields : []
		const fields = this.definition.fields.sortBy("order")
			.map((field: FieldInstance) => {
				return {
					key: "fields." + field.field.id,
					label: field.field.title,
					dataType: field.field.dataType,
					dataSourceId: field.field.dataSourceId,
					extensions: field.field.extensions,
					section: field.section,
					errors: (item: any) => !field.requiredRecord || (item.fields && item.fields[field.field.id!]) ? null : "Please enter " + field.field.title
				}
			})

		return [
			...specialFields,
			...fields
		]
	}

	public get dataModalTitle (): string {
		return this.dataSource ? this.dataSource.title : ""
	}

	private get loadAll () {
		return this.dataSource ? this.dataSource.itemCount < 250 : true
	}

	public async mounted () {
		if (this.dataSourceId) {
			this.dataSource = await api.dataSourceGet(this.dataSourceId)

			if (this.dataSource) {
				this.definition = this.dataSource.definitionId
					? await api.definitionDetails(this.dataSource.definitionId)
					: null
			}

			if (this.loadAll) {
				this.dataSourceOptions = await api.dataSourceGetAll(this.dataSource.id, this.ignorePermissions)
			} else if (this.filterField) {
				await this.filterFieldValueChanged(this.filterField.value)
			}
		}

		if (this.value) {
			if (this.multiple) {
				this.selectedItem = []
				for (var i = 0; i < this.value.length; i++) {
					const value = this.value[i]

					const selectedOption = this.dataSourceOptions.find(item => item.id === value.id)
					if (selectedOption) {
						this.selectedItem.push(selectedOption)
					} else {
						this.dataSourceOptions.push(value)
						this.selectedItem.push(value)
					}
				}
			} else {
				const selectedOption = this.dataSourceOptions.find(item => item.id === this.value)
				if (selectedOption) {
					this.selectedItem = selectedOption
				} else {
					try {
						this.selectedItem = await api.dataSourceGetItem(this.value)
					} catch (e) {
						console.error(e)
						if (this.currentCase && this.currentCase.fields && this.currentCase.fields[this.field.id!]) {
							this.selectedItem = {
								label: this.currentCase.fields[this.field.id!].label
									? this.currentCase.fields[this.field.id!].label
									: "[Item not found]",
								value: this.currentCase.fields[this.field.id!].value,
								itemDeleted: true
							}
						} else {
							this.selectedItem = {
								label: "[Deleted item]",
								value: this.value,
								itemDeleted: true
							}
						}
						this.loading = false
						return
					}
					if (!this.dataSourceOptions.find(item => item.id === this.value)) {
						this.dataSourceOptions.push(this.selectedItem)
					}
				}
			}
		} else {
			this.selectedItem = null
		}

		this.loading = false
	}

	@Watch("value")
	public async onValueChanged (newValue: any) {
		if (this.multiple) {
			if (!newValue) {
				this.selectedItem = []
			} else {
				const items = []

				for (var i = 0; i < newValue.length; i++) {
					const item = newValue[i]
					const existingItem = this.selectedItem
						? this.selectedItem.find((existingItem: any) => existingItem.id === item.id)
						: null

					if (existingItem) {
						items.push(existingItem)
					} else {
						const selectedOption = this.dataSourceOptions.find(option => option.id === item.id)
						if (selectedOption) {
							items.push(selectedOption)
						} else {
							this.dataSourceOptions.push(item)
							this.selectedItem.push(item)
						}
					}
				}

				this.selectedItem = items
			}
		} else {
			if (!newValue) {
				this.selectedItem = null
			}

			if (!this.loadAll) {
				this.dataSourceOptions = []
			}

			if (!this.selectedItem || (this.selectedItem && newValue.id !== this.selectedItem.id)) {
				const selectedOption = this.dataSourceOptions.find(item => item.id === this.value)

				if (selectedOption) {
					this.selectedItem = selectedOption
				} else {
					if (this.value) {
						try {
							this.selectedItem = await api.dataSourceGetItem(this.value)
						} catch (e) {
							console.error(e)
							if (this.currentCase && this.currentCase.fields && this.currentCase.fields[this.field.id!]) {
								this.selectedItem = {
									label: this.currentCase.fields[this.field.id!].label,
									value: this.currentCase.fields[this.field.id!].value
								}
							} else {
								this.selectedItem = null
							}
							return
						}

						if (!this.dataSourceOptions.find(item => item.id === this.value)) {
							this.dataSourceOptions.push(this.selectedItem)
						}
					}
				}
			}
		}
	}

	public async fetchOptions (search: string, loading: (isLoading: boolean) => void) {
		if (this.loadAll) return

		if (search === "") {
			if (this.value) {
				this.dataSourceOptions = []
				if (Array.isArray(this.selectedItem)) {
					this.selectedItem.forEach(selectedItem => {
						if (!this.dataSourceOptions.find(item => item.id === selectedItem.id)) {
							this.dataSourceOptions.push(selectedItem)
						}
					})
				} else {
					if (this.selectedItem && !this.dataSourceOptions.find(item => item.id === this.value)) {
						this.dataSourceOptions.push(this.selectedItem)
					}
				}
			}
		} else {
			loading(true)
			this.dataSourceOptions = (await api.dataSourceSearch(this.dataSourceId, null, search)).data
			loading(false)
		}
	}

	public async valueChanged (newValue: any) {
		this.selectedItem = newValue

		if (newValue) {
			if (Array.isArray(newValue)) {
				this.$emit("input", newValue.map(value => {
					return {
						id: value.id,
						label: value.label
					}
				}))
			} else {
				this.$emit("input", newValue.id)
				this.$emit("inputLabel", newValue.label)
			}
		} else {
			this.$emit("input", null)
			this.$emit("inputLabel", null)
		}

		this.$emit("inputValue", newValue)
	}

	public async addNewDataSourceItem () {
		if (!this.dataSource) {
			return
		}

		this.$refs.dataModal.open({
			dataSourceId: this.dataSource.id,
			fields: {}
		})
	}

	public async saveNewDataSourceItem (item: any) {
		if (!item) {
			return
		}

		const newItem = await api.dataSourceCreateItem(item)

		if (this.dataSource && this.loadAll) {
			this.dataSourceOptions = await api.dataSourceGetAll(this.dataSource.id)
		}

		if (newItem && newItem.id) {
			if (this.multiple) {
				this.valueChanged([...this.selectedItem ? this.selectedItem : [], newItem])
			} else {
				this.valueChanged(newItem)
			}
		}
	}

	private alignDropdownRight (dropdownList: HTMLUListElement, component: any, { width }: { width: string }) {
		dropdownList.style.width = "auto"
		dropdownList.style.minWidth = width
		dropdownList.style.maxWidth = MAX_WIDTH

		const popper = createPopper(component.$refs.toggle, dropdownList, { placement: "bottom-end"	})
		return () => popper.destroy()
	}
}
